import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Copy, Plus, Trash2, Link2, QrCode, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";

export default function InvitesDialog({ trigger }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const qrRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/invites");
      setItems(data);
    } catch (err) {
      console.error("Failed to load invites:", err);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    try {
      await api.post("/invites");
      toast.success("Invite link generated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (code) => {
    await api.delete(`/invites/${code}`);
    if (qrCode === code) setQrCode(null);
    load();
  };

  const linkFor = (code) => `${window.location.origin}/login?invite=${code}`;

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(linkFor(code));
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadQr = (code) => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `cfaa-invite-${code}.png`;
    a.click();
    toast.success("QR code downloaded");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-3xl max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Link2 className="w-5 h-5 text-purple-700" /> Invite links
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">Share these links or QR codes to invite people to join CFAA.</p>
        <Button onClick={create} data-testid="invite-create-btn" className="rounded-2xl bg-purple-700 hover:bg-purple-800 h-11 font-bold">
          <Plus className="w-4 h-4 mr-1" /> Generate new invite link
        </Button>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-6">No invite links yet</p>
          )}
          {items.map((inv) => (
            <div key={inv.code} data-testid={`invite-row-${inv.code}`} className="bg-slate-50 rounded-2xl p-3 border border-slate-100">
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono text-slate-700 truncate">{linkFor(inv.code)}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{inv.uses} {inv.uses === 1 ? "use" : "uses"} · by {inv.created_by_name}</p>
                </div>
                <button onClick={() => setQrCode(qrCode === inv.code ? null : inv.code)} data-testid={`invite-qr-${inv.code}`} className={`w-8 h-8 rounded-xl flex items-center justify-center ${qrCode === inv.code ? "bg-purple-700 text-white" : "bg-white hover:bg-purple-100 text-purple-800"}`} title="QR code">
                  <QrCode className="w-4 h-4" />
                </button>
                <button onClick={() => copy(inv.code)} data-testid={`invite-copy-${inv.code}`} className="w-8 h-8 rounded-xl bg-white hover:bg-purple-100 text-purple-800 flex items-center justify-center" title="Copy">
                  <Copy className="w-4 h-4" />
                </button>
                <button onClick={() => remove(inv.code)} data-testid={`invite-delete-${inv.code}`} className="w-8 h-8 rounded-xl bg-white hover:bg-red-100 text-red-500 flex items-center justify-center" title="Revoke">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {qrCode === inv.code && (
                <div data-testid={`invite-qr-panel-${inv.code}`} className="mt-3 flex flex-col items-center gap-3 pt-3 border-t border-slate-200">
                  <div ref={qrRef} className="bg-white p-3 rounded-2xl border border-slate-200">
                    <QRCodeCanvas value={linkFor(inv.code)} size={180} level="M" fgColor="#5b21b6" includeMargin />
                  </div>
                  <Button onClick={() => downloadQr(inv.code)} data-testid={`invite-qr-download-${inv.code}`} className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold h-9 px-4">
                    <Download className="w-4 h-4 mr-1" /> Download PNG
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
